import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X as XIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  usePushState,
  subscribeToPush,
} from "@/components/pushClient";

/**
 * Post-signup push prompt banner 🔔
 * Shown once per user, after they're signed in, IF:
 *  - browser supports push
 *  - permission state is "default" (never asked yet)
 *  - they aren't already subscribed
 *  - they haven't dismissed it (localStorage flag)
 *
 * Sits at the bottom of the screen, never covers content. One soft prompt —
 * no nag loop.
 */
const DISMISS_KEY = "omni_push_prompt_dismissed";

export default function PushPromptBanner() {
  const { user } = useAuth();
  const { perm, subscribed, supported, refresh } = usePushState();
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem(DISMISS_KEY) === "1"
  );

  // Re-check periodically in case the user takes time to interact with the
  // permission dialog or comes back from another tab.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [user, refresh]);

  function dismiss() {
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {}
    setDismissed(true);
  }

  async function enable() {
    if (busy) return;
    setBusy(true);
    try {
      await subscribeToPush();
      toast.success("🔔 Browser notifications enabled. You're set.");
      dismiss();
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Couldn't enable push notifications.");
      // Don't dismiss on failure — user may want to try again or check settings
    } finally {
      setBusy(false);
    }
  }

  if (!user) return null;
  if (dismissed) return null;
  if (!supported) return null;
  if (perm !== "default") return null; // already decided
  if (subscribed) return null;

  return (
    <div
      data-testid="push-prompt-banner"
      className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-4 sm:max-w-sm z-[70] bg-[#0F0F0F] border-2 border-[#D4AF37] shadow-[0_8px_40px_rgba(0,0,0,0.85)] p-4"
    >
      <button
        type="button"
        data-testid="push-prompt-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 text-neutral-500 hover:text-white"
      >
        <XIcon size={16} />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="text-3xl leading-none shrink-0">🔔</div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-0.5">
            Stay in the loop
          </div>
          <h3 className="font-display text-base text-white leading-snug">
            Get streak & DM pings <span className="text-[#D4AF37]">without opening the app.</span>
          </h3>
          <p className="text-[11px] text-neutral-500 mt-1 leading-relaxed">
            Browser notifications. One tap. Disable any time from Profile → Preferences.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              data-testid="push-prompt-enable"
              onClick={enable}
              disabled={busy}
              className="btn-omni btn-red text-xs uppercase tracking-widest disabled:opacity-40"
            >
              {busy ? "Enabling…" : "Allow"}
            </button>
            <button
              type="button"
              data-testid="push-prompt-later"
              onClick={dismiss}
              className="text-xs uppercase tracking-widest text-neutral-500 hover:text-white"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
